import { useEffect, useState, useCallback } from "react";
import { adminLogin, getToken, clearToken, fetchClosePortfolio, api } from "@/lib/api";

const inputCls = "w-full bg-transparent border border-[rgba(180,139,63,0.35)] px-3 py-2 text-[var(--bone)] outline-none focus:border-[var(--gold)] mb-2";
const errMsg = (d, f) => (typeof d === "string" ? d : d?.message || d?.detail || f || "Something went wrong.");
const SCENARIOS = ["COMMITTED_ONLY", "BASELINE", "CONSERVATIVE", "EXPECTED", "OPPORTUNITY", "STRESS"];
const Unknown = () => <span className="italic text-[#e88f6a]">UNKNOWN</span>;
// Format a decimal-string amount with thousands separators + explicit currency code. UNKNOWN stays UNKNOWN
// (never rendered as $0); missing/unknown currency is labelled, never silently defaulted.
const fmtMoney = (v, currency) => {
  if (v == null) return null;
  const n = Number(v);
  const body = Number.isFinite(n)
    ? n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    : String(v);
  return `${body} ${currency || "UNKNOWN CCY"}`;
};
const money = (v, currency) => (v == null ? <Unknown /> : fmtMoney(v, currency));

function Login({ onIn }) {
  const [email, setEmail] = useState(""); const [pw, setPw] = useState(""); const [err, setErr] = useState(null);
  const submit = async (e) => { e.preventDefault(); setErr(null);
    try { await adminLogin(email, pw); onIn(); } catch (e) { setErr(errMsg(e.response?.data?.detail, "Login failed.")); } };
  return (
    <div className="min-h-screen bg-[var(--navy)] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="treasury-login-form">
        <h1 className="font-display uppercase tracking-wide2 text-[var(--gold)] text-xl mb-4">Founder Treasury</h1>
        <input className={inputCls} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="treasury-login-email" aria-label="Email" />
        <input className={inputCls} type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="treasury-login-password" aria-label="Password" />
        <button className="jmh-cta mt-2" data-testid="treasury-login-submit">Enter</button>
        {err && <p className="mt-3 text-sm text-[#e88f6a]" data-testid="treasury-login-error">{err}</p>}
      </form>
    </div>
  );
}

function Console({ onSignOut }) {
  const [entities, setEntities] = useState([]); const [eid, setEid] = useState("");
  const [scenario, setScenario] = useState("BASELINE");
  const [d, setD] = useState(null); const [err, setErr] = useState(null);
  useEffect(() => { fetchClosePortfolio().then((r) => {
    const list = (r.entities || []).map((e) => ({ id: e.entity_id, name: e.entity_name || e.legal_name || e.entity_id }));
    setEntities(list); if (!list.length) setErr("No financial entities are available in this workspace yet. Verified source records are needed before this view can be calculated."); setEid((prev) => prev || (list[0] && list[0].id) || "");
  }).catch((e) => setErr(errMsg(e.response?.data?.detail))); }, []);
  const load = useCallback(() => { if (!eid) return; setD(null); setErr(null);
    api.get(`/fin/treasury/entities/${eid}/forecast?scenario=${scenario}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setD(r.data)).catch((e) => setErr(errMsg(e.response?.data?.detail, "Treasury forecast unavailable."))); }, [eid, scenario]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[var(--navy)] text-[var(--bone)]">
      <header className="border-b border-[rgba(180,139,63,0.2)] px-4 md:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-display uppercase tracking-wide2 text-[var(--gold)] text-lg">Founder Treasury · 13-Week Cash</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <select className="bg-transparent border border-[rgba(180,139,63,0.3)] text-xs px-2 py-1" data-testid="treasury-entity-select" aria-label="Entity" value={eid} onChange={(e) => setEid(e.target.value)}>
            {entities.map((e) => <option key={e.id} value={e.id}>{`${e.name} · ${e.id.slice(0, 6)}`}</option>)}
          </select>
          <select className="bg-transparent border border-[rgba(180,139,63,0.3)] text-xs px-2 py-1" data-testid="treasury-scenario-select" aria-label="Scenario" value={scenario} onChange={(e) => setScenario(e.target.value)}>
            {SCENARIOS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={onSignOut} className="font-display uppercase tracking-wide2 text-xs text-[var(--gold-lite)]" data-testid="treasury-signout">Sign out</button>
        </div>
      </header>
      <main className="px-4 md:px-8 py-6 max-w-6xl">
        {err ? <p className="text-sm text-[#e88f6a]" data-testid="treasury-error">{err}</p>
        : !d ? <p className="text-sm text-[var(--muted)]" data-testid="treasury-loading">Loading forecast…</p>
        : (
          <div data-testid="treasury-view">
            <p className="text-[11px] text-[#e7a877] mb-1" data-testid="treasury-expected-note">{d.expected_cash_note}</p>
            <p className="text-[11px] text-[var(--muted)] mb-1">{d.authoritative_cash_note}</p>
            {d.runway && (
              <section className="mb-4 border border-[rgba(180,139,63,0.18)] px-4 py-3" data-testid="treasury-runway">
                <div className="text-[10px] uppercase tracking-wide2 text-[var(--muted)] mb-1">Liquidity runway</div>
                <div className="text-sm text-[var(--bone)]">
                  {d.runway.runway_status === "CALCULABLE" ? `${d.runway.weeks_of_liquidity} weeks · first shortfall ${d.runway.first_projected_shortfall_week}`
                   : d.runway.runway_status === "NO_SHORTFALL_WITHIN_13_WEEKS" ? "No shortfall within 13 weeks"
                   : <span className="text-[#e88f6a]">Not calculable · {d.runway.runway_status}</span>}
                </div>
                <div className="text-[10px] text-[var(--muted)] mt-1">{d.runway.runway_disclaimer}</div>
              </section>
            )}
            <p className="text-[11px] text-[var(--muted)] mb-4">Scenario <span className="text-[var(--gold-lite)]">{d.scenario}</span> · {d.scenario_disclaimer} · cadence {d.cadence} · as of {d.as_of}</p>

            <section className="mb-6" data-testid="treasury-cash-position">
              <h2 className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80 mb-2">Authoritative cash position</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[["Reconciled available", d.cash_position.reconciled_available_cash], ["Restricted", d.cash_position.restricted_cash],
                  ["Unrestricted", d.cash_position.unrestricted_cash], ["Restriction UNKNOWN", d.cash_position.restriction_unknown_cash]].map(([l, v], i) => (
                  <div key={i} className="border border-[rgba(180,139,63,0.18)] px-4 py-3">
                    <div className="text-[10px] uppercase tracking-wide2 text-[var(--muted)] mb-1">{l}</div>
                    <div className="text-sm text-[var(--bone)]">{money(v, d.cash_position.opening_currency)}</div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-[#e88f6a] mt-2" data-testid="treasury-opening-status">Opening cash: {d.opening_cash_status} · reconciliation {d.cash_position.reconciliation_receipt_id ? "linked" : "none"} · FX {d.cash_position.fx_status}</p>
            </section>

            <section className="mb-6" data-testid="treasury-signals">
              <h2 className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80 mb-2">Liquidity signals</h2>
              <ul className="border border-[rgba(180,139,63,0.18)] divide-y divide-[rgba(180,139,63,0.1)]">
                {d.liquidity_signals.map((s, i) => <li key={i} className="px-4 py-2 text-sm text-[#e7a877]" data-testid={`treasury-signal-${i}`}>{s.signal}{s.reason_code ? ` · ${s.reason_code}` : ""}{s.first_week ? ` · ${s.first_week}` : ""}</li>)}
              </ul>
            </section>

            <section data-testid="treasury-weeks" className="overflow-x-auto">
              <h2 className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80 mb-2">13-week projection</h2>
              <table className="w-full text-sm border border-[rgba(180,139,63,0.18)]">
                <thead><tr className="text-[10px] uppercase tracking-wide2 text-[var(--muted)] text-left">
                  <th className="px-3 py-2">Week</th><th className="px-3 py-2">Incl. inflows</th><th className="px-3 py-2">Incl. outflows</th>
                  <th className="px-3 py-2">Net</th><th className="px-3 py-2">Ending cash</th><th className="px-3 py-2">Status</th></tr></thead>
                <tbody>
                  {d.weeks.map((w) => (
                    <tr key={w.week_index} className="border-t border-[rgba(180,139,63,0.1)]" data-testid={`treasury-week-${w.week_index}`}>
                      <td className="px-3 py-2 text-[var(--muted)]">{w.week_start}</td>
                      <td className="px-3 py-2">{fmtMoney(w.included_inflows, d.cash_position.opening_currency)}</td>
                      <td className="px-3 py-2">{fmtMoney(w.included_outflows, d.cash_position.opening_currency)}</td>
                      <td className="px-3 py-2">{fmtMoney(w.net_cash_change, d.cash_position.opening_currency)}</td>
                      <td className="px-3 py-2">{money(w.projected_ending_cash, d.cash_position.opening_currency)}</td>
                      <td className="px-3 py-2 text-[10px]">{w.result_status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {d.unknown_or_unclassified_items.length > 0 && (
              <section className="mt-6" data-testid="treasury-unknowns">
                <h2 className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80 mb-2">UNKNOWN / unclassified · {d.unknown_or_unclassified_items.length}</h2>
                <ul className="border border-[rgba(180,139,63,0.18)] divide-y divide-[rgba(180,139,63,0.1)]">
                  {d.unknown_or_unclassified_items.slice(0, 30).map((u, i) => <li key={i} className="px-4 py-2 text-sm text-[#e88f6a]">{u.ref} · {u.stage} · {u.reason}</li>)}
                </ul>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default function FounderTreasury() {
  const [authed, setAuthed] = useState(!!getToken());
  if (!authed) return <Login onIn={() => setAuthed(true)} />;
  return <Console onSignOut={() => { clearToken(); setAuthed(false); }} />;
}
